import { startAuthentication, startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';

const routes = window.passkeyRoutes || {};
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

function postJson(url, payload = {}) {
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-TOKEN': csrfToken,
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  }).then(async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Something went wrong. Please try again.');
    }
    return data;
  });
}

function showError(element, message) {
  if (!element) {
    return;
  }
  element.textContent = message;
  element.classList.remove('d-none');
}

function hideError(element) {
  if (!element) {
    return;
  }
  element.textContent = '';
  element.classList.add('d-none');
}

function guessDeviceLabel() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone · Face ID';
  if (/iPad/.test(ua)) return 'iPad · Face ID';
  if (/Macintosh|Mac OS/.test(ua)) return 'MacBook Pro · Touch ID';
  if (/Windows/.test(ua)) return 'Windows PC · Windows Hello';
  if (/Android/.test(ua)) return 'Android · Fingerprint';
  return 'Security key';
}

function rememberCredentialId(credentialId) {
  if (credentialId) {
    localStorage.setItem('itsrapid_passkey_id', credentialId);
  }
}

function markCurrentDevicePasskeys() {
  const currentId = localStorage.getItem('itsrapid_passkey_id');
  if (!currentId) {
    return;
  }

  document.querySelectorAll('[data-credential-id]').forEach((item) => {
    if (item.dataset.credentialId === currentId) {
      const badge = item.querySelector('[data-this-device-badge]');
      badge?.classList.remove('d-none');
    }
  });
}

async function registerPasskey({ button, errorElement, onSuccess, hintElement }) {
  if (!browserSupportsWebAuthn()) {
    showError(errorElement, 'Your browser does not support passkeys.');
    return;
  }

  const originalText = button?.textContent;
  button?.setAttribute('disabled', 'disabled');
  if (button) {
    button.textContent = 'Waiting for your device…';
  }
  hideError(errorElement);
  hintElement?.classList.remove('d-none');

  try {
    const options = await postJson(routes.registerOptions);
    const credential = await startRegistration({ optionsJSON: options });
    const result = await postJson(routes.registerVerify, {
      credential,
      device_label: guessDeviceLabel(),
    });

    rememberCredentialId(result.credentialId);
    if (typeof onSuccess === 'function') {
      onSuccess(result);
    } else if (result.redirect) {
      window.location.href = result.redirect;
    }
  } catch (error) {
    showError(errorElement, error.message || 'Could not add passkey.');
  } finally {
    hintElement?.classList.add('d-none');
    button?.removeAttribute('disabled');
    if (button && originalText) {
      button.textContent = originalText;
    }
  }
}

async function loginWithPasskey(email, { button, errorElement, remember = false }) {
  if (!browserSupportsWebAuthn()) {
    showError(errorElement, 'Your browser does not support passkeys.');
    return;
  }

  const originalText = button?.innerHTML;
  button?.setAttribute('disabled', 'disabled');
  hideError(errorElement);

  try {
    const options = await postJson(routes.loginOptions, { email });
    const credential = await startAuthentication({ optionsJSON: options });
    const result = await postJson(routes.loginVerify, { credential, remember });
    if (result.redirect) {
      window.location.href = result.redirect;
    }
  } catch (error) {
    showError(errorElement, error.message || 'Passkey sign-in failed.');
  } finally {
    button?.removeAttribute('disabled');
    if (button && originalText) {
      button.innerHTML = originalText;
    }
  }
}

function initLoginPage() {
  const root = document.querySelector('[data-passkey-login]');
  if (!root || !routes.checkEmail) {
    return;
  }

  const passkeyView = document.getElementById('login-passkey-view');
  const passwordView = document.getElementById('login-password-view');
  const passkeyEmail = document.getElementById('passkey-email');
  const passwordEmail = document.getElementById('email');
  const passwordInput = document.getElementById('password');
  const passkeySubmit = document.getElementById('passkey-login-submit');
  const showPasswordButton = document.getElementById('passkey-show-password');
  const rememberCheckbox = document.getElementById('remember');
  const errorElement = document.getElementById('passkey-login-error');
  const loginForm = passwordEmail?.closest('form');

  // Prevent the passkey / "Another Method" view from flashing during password sign-in.
  // checkEmail is async; blur-on-submit or a slow initial check can resolve after the
  // user has already chosen password login or started submitting the form.
  let passwordPreferred = false;
  let isSubmitting = false;
  let checkRequestId = 0;
  let lastCheckedEmail = '';

  const syncEmails = (value) => {
    if (passkeyEmail) passkeyEmail.value = value;
    if (passwordEmail) passwordEmail.value = value;
  };

  const showPasskeyView = () => {
    passkeyView?.classList.remove('d-none');
    passwordView?.classList.add('d-none');
  };

  const showPasswordView = () => {
    passkeyView?.classList.add('d-none');
    passwordView?.classList.remove('d-none');
  };

  const shouldSkipPasskeyPrompt = () => {
    if (passwordPreferred || isSubmitting) {
      return true;
    }

    // Don't yank the UI away if the user is already entering a password.
    if (passwordInput?.value && passwordView && !passwordView.classList.contains('d-none')) {
      return true;
    }

    return false;
  };

  const checkEmail = async (email) => {
    if (!email || !email.includes('@')) {
      showPasswordView();
      return;
    }

    const requestId = ++checkRequestId;
    lastCheckedEmail = email;

    try {
      const result = await postJson(routes.checkEmail, { email });

      if (requestId !== checkRequestId || shouldSkipPasskeyPrompt()) {
        return;
      }

      if (result.hasPasskeys) {
        syncEmails(email);
        showPasskeyView();
      } else {
        showPasswordView();
      }
    } catch (error) {
      if (requestId !== checkRequestId || isSubmitting) {
        return;
      }
      showPasswordView();
    }
  };

  passkeyEmail?.addEventListener('blur', () => {
    if (isSubmitting) {
      return;
    }
    checkEmail(passkeyEmail.value.trim());
  });

  passwordEmail?.addEventListener('blur', () => {
    // Blur fires before submit when clicking Sign In — never reopen passkey view then.
    if (isSubmitting || passwordPreferred) {
      return;
    }

    const email = passwordEmail.value.trim();
    syncEmails(email);
    checkEmail(email);
  });

  passwordEmail?.addEventListener('input', () => {
    if (passwordEmail.value.trim() !== lastCheckedEmail) {
      passwordPreferred = false;
    }
  });

  showPasswordButton?.addEventListener('click', () => {
    passwordPreferred = true;
    syncEmails(passkeyEmail?.value.trim() || '');
    showPasswordView();
  });

  const invalidatePasskeyCheck = () => {
    passwordPreferred = true;
    // Invalidate any in-flight checkEmail so a late response cannot flash the UI.
    checkRequestId += 1;
  };

  // mousedown runs before the email/password blur when clicking Sign In.
  loginForm?.querySelector('[type="submit"]')?.addEventListener('mousedown', invalidatePasskeyCheck);
  loginForm?.addEventListener('submit', () => {
    isSubmitting = true;
    invalidatePasskeyCheck();
  });

  passkeySubmit?.addEventListener('click', () => {
    const email = passkeyEmail?.value.trim();
    if (!email) {
      showError(errorElement, 'Please enter your email address.');
      return;
    }
    loginWithPasskey(email, {
      button: passkeySubmit,
      errorElement,
      remember: rememberCheckbox?.checked ?? false,
    });
  });

  const initialEmail = passkeyEmail?.value.trim() || passwordEmail?.value.trim();
  if (initialEmail) {
    checkEmail(initialEmail);
  }
}

function initOfferPage() {
  const button = document.getElementById('passkey-offer-add');
  const errorElement = document.getElementById('passkey-offer-error');
  const hintElement = document.getElementById('passkey-browser-hint');
  if (!button) {
    return;
  }

  button.addEventListener('click', () => {
    registerPasskey({ button, errorElement, hintElement });
  });
}

function initAccountPage() {
  const button = document.getElementById('passkey-account-add');
  const errorElement = document.getElementById('passkey-account-error');
  if (!button) {
    markCurrentDevicePasskeys();
    return;
  }

  button.addEventListener('click', () => {
    registerPasskey({
      button,
      errorElement,
      hintElement: document.getElementById('passkey-browser-hint'),
      onSuccess: (result) => {
        if (result.redirect) {
          window.location.href = result.redirect;
        } else {
          window.location.reload();
        }
      },
    });
  });

  markCurrentDevicePasskeys();
}

document.addEventListener('DOMContentLoaded', () => {
  initLoginPage();
  initOfferPage();
  initAccountPage();

  if (window.location.hash === '#passkeys') {
    document.getElementById('passkeys-tab')?.click();
  }
});
